import { motion } from "framer-motion";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";

// Landing · HomeIQ Score panel.
// Single source of truth: `homeIQScore` from HomeIQContext, produced by
// engine/homeIQScore.js (Tier 7 composite). Vastu is intentionally excluded
// from the composite at the engine level, so the pillar list below has 4
// entries — matching the tabs and PDF report.
//
// Gating rule (product decision · Option C): if the user has not entered
// rent yet, we do NOT display seeded-default numbers. Instead we render a
// "ready state" that prompts them to enter rent on the RentDiscovery card
// above. This prevents users from mistaking placeholder maths for their own
// personalised HomeIQ score.

export default function HomeIQScore() {
    const { rent, landingHomeIQScore } = useHomeIQ();
    const hasRent = rent != null && rent > 0;

    return (
        <section
            id="score"
            data-testid="homeiq-score-section"
            className="relative py-16 md:py-24 px-6 md:px-12 bg-[#F7F8FB] border-t border-[#0A1F44]/5"
        >
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                <div className="lg:col-span-5">
                    <p className="text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] mb-5 font-semibold">
                        — Your HomeIQ score
                    </p>
                    <h2
                        data-testid="score-heading"
                        className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[#0A1F44] mb-6"
                    >
                        One score.
                        <br />
                        <span className="text-[#0A1F44]/45">
                            Four honest answers.
                        </span>
                    </h2>
                    <p className="text-[#0A1F44]/65 text-base sm:text-lg leading-relaxed max-w-md">
                        Your HomeIQ score is the honest answer to &ldquo;should I buy
                        this home?&rdquo; It grows clearer as we learn more about
                        you — and it&apos;s something you can share with your
                        partner, your parents, even your lender.
                    </p>

                    <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-[11px] tracking-[0.22em] uppercase text-[#0A1F44]/55 font-semibold">
                        <span>Honest</span>
                        <span>·</span>
                        <span>Always current</span>
                        <span>·</span>
                        <span>Yours to share</span>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="relative rounded-3xl bg-white border border-[#0A1F44]/10 shadow-[0_30px_80px_-40px_rgba(10,31,68,0.3)] p-8 md:p-10 overflow-hidden"
                    >
                        <div className="absolute inset-0 blueprint-grid opacity-20 pointer-events-none" />
                        <div className="relative z-10 flex items-start justify-between mb-8">
                            <div>
                                <div className="text-[10px] tracking-[0.28em] uppercase text-[#0A1F44]/50 font-semibold mb-1.5">
                                    Your HomeIQ score
                                </div>
                                <div className="font-display font-bold text-xl text-[#0A1F44]">
                                    Built from four honest answers
                                </div>
                            </div>
                            <div className="text-right">
                                <div
                                    data-testid="composite-score"
                                    className="font-display font-extrabold text-6xl md:text-7xl text-[#0A1F44] tabular-nums leading-none"
                                >
                                    {hasRent ? landingHomeIQScore.score : "—"}
                                </div>
                                <div className="mt-1 text-[10px] tracking-[0.25em] uppercase text-[#1E6BFF] font-semibold">
                                    /100 · {hasRent ? landingHomeIQScore.verdict : "Pending"}
                                </div>
                            </div>
                        </div>

                        <div className="relative z-10 space-y-4">
                            {landingHomeIQScore.pillars.map((p) => (
                                <div
                                    key={p.id}
                                    data-testid={`pillar-${p.id}`}
                                    className="grid grid-cols-12 items-center gap-4"
                                >
                                    <div className="col-span-4 sm:col-span-3 text-[11px] tracking-[0.22em] uppercase text-[#0A1F44]/55 font-semibold">
                                        {p.label}
                                    </div>
                                    <div className="col-span-6 sm:col-span-8 h-1.5 rounded-full bg-[#0A1F44]/8 overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            whileInView={{
                                                width: hasRent && p.included ? `${p.score}%` : "0%",
                                            }}
                                            viewport={{ once: false }}
                                            transition={{
                                                duration: 1.2,
                                                ease: [0.16, 1, 0.3, 1],
                                            }}
                                            className="h-full bg-gradient-to-r from-[#0A1F44] to-[#1E6BFF]"
                                        />
                                    </div>
                                    <div className="col-span-2 sm:col-span-1 text-right font-display font-bold text-[#0A1F44] tabular-nums">
                                        {hasRent && p.included ? p.score : "—"}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {!hasRent && (
                            <div
                                data-testid="score-empty-hint"
                                className="relative z-10 mt-8 pt-6 border-t border-[#0A1F44]/8 text-[11px] tracking-[0.18em] uppercase text-[#0A1F44]/50 font-semibold text-center"
                            >
                                Enter your rent above to see your live HomeIQ score.
                            </div>
                        )}

                        {hasRent && (
                            <p
                                data-testid="score-caption"
                                className="relative z-10 mt-8 pt-6 border-t border-[#0A1F44]/8 text-[11px] text-[#0A1F44]/55 leading-relaxed text-center"
                            >
                                Estimated using a typical buyer profile.
                                Personalise your numbers in the Affordability Assessment.
                            </p>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
